import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';
import { ChevronLeftIcon, ChevronRightIcon, HomeIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Share2 } from 'lucide-react';

const EvangelioPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDarkMode: darkMode } = useDarkMode();
  const { userInfo, userType, token } = useAuth();
  const { t, formatDate } = useLocalization();
  
  const [gospelData, setGospelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [viewCount, setViewCount] = useState(null);
  const [viewCountLoading, setViewCountLoading] = useState(false);

  // Share state
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareCapabilities, setShareCapabilities] = useState({
    webShare: false,
    clipboard: false,
    browserName: 'Unknown'
  });

  // Helper function to get today's date
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Note: formatDate function is now provided by useLocalization hook

  // Helper function to clean gospel text
  const cleanGospelText = (verses) => {
    if (!verses || !Array.isArray(verses)) {
      return [];
    }
    
    const seenVerses = new Set();
    const cleanedVerses = [];
    
    verses.forEach(verse => {
      if (typeof verse === 'string') {
        let cleanedVerse = verse
          .replace(/□/g, '')
          .replace(/\u0080\u009c/g, '"')
          .replace(/\u0080\u009d/g, '"')
          .replace(/\u0080\u0099/g, "'")
          .replace(/\u00c3\u00a9/g, 'é')
          .replace(/\u00c3\u00b1/g, 'ñ')
          .replace(/\u00c3\u00ad/g, 'í')
          .trim();
        
        const normalizedVerse = cleanedVerse.replace(/\s+/g, ' ').toLowerCase();
        
        if (!seenVerses.has(normalizedVerse) && cleanedVerse.length > 10) {
          seenVerses.add(normalizedVerse);
          cleanedVerses.push(cleanedVerse);
        }
      }
    });
    
    return cleanedVerses;
  };

  // Calculate date (add or subtract days)
  const calculateDate = (baseDate, daysDiff) => {
    try {
      const date = new Date(baseDate + 'T00:00:00');
      date.setDate(date.getDate() + daysDiff);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error calculating date:', error);
      return baseDate;
    }
  };

  // Navigate to different date
  const navigateDate = (direction) => {
    if (!currentDate) return;
    
    const newDate = calculateDate(currentDate, direction);
    setSearchParams({ date: newDate });
  };

  // Track evangelio view (for non-admin users)
  const trackEvangelioView = async (date) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Prepare headers with auth token if available
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`${backendUrl}/api/evangelio-views/increment?date=${encodeURIComponent(date)}`, {
        method: 'POST',
        headers: headers
      });
      
      // Note: No need to handle response since we don't want to show errors to users
      // View tracking should be silent
    } catch (error) {
      // Silent fail - view tracking shouldn't interfere with user experience
      console.log('View tracking failed:', error);
    }
  };

  // Fetch view count for admin users
  const fetchViewCount = async (date) => {
    // Only fetch view count for admin users
    if (!userType || userType !== 'admin') {
      return;
    }
    
    try {
      setViewCountLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${backendUrl}/api/admin/evangelio-views/${encodeURIComponent(date)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const viewData = await response.json();
        setViewCount(viewData.count);
      } else {
        console.log('Failed to fetch view count:', response.status);
        setViewCount(null);
      }
    } catch (error) {
      console.log('Error fetching view count:', error);
      setViewCount(null);
    } finally {
      setViewCountLoading(false);
    }
  };

  // Fetch gospel data
  const fetchGospelData = async (targetDate) => {
    try {
      setLoading(true);
      setError(null);
      setViewCount(null); // Reset view count when loading new date
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/evangelio-del-dia?date=${encodeURIComponent(targetDate)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(t('gospelNotAvailable', { date: targetDate }));
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setGospelData(data);
      setCurrentDate(data.date || targetDate);
      
      // Track view for non-admin users (includes anonymous visitors)
      await trackEvangelioView(data.date || targetDate);
      
      // Fetch view count for admin users
      await fetchViewCount(data.date || targetDate);
      
    } catch (err) {
      console.error('Error loading gospel:', err);
      setError(t('gospelLoadError'));
    } finally {
      setLoading(false);
    }
  };

  // Detect browser capabilities on mount
  useEffect(() => {
    const detectCapabilities = () => {
      const hasWebShare = !!(navigator.share && navigator.canShare);
      const hasClipboard = !!(navigator.clipboard && window.isSecureContext);
      
      // Simple browser detection
      let browserName = 'Unknown';
      if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
        browserName = 'Safari';
      } else if (navigator.userAgent.includes('Chrome')) {
        browserName = 'Chrome';
      } else if (navigator.userAgent.includes('Firefox')) {
        browserName = 'Firefox';
      } else if (navigator.userAgent.includes('Edge')) {
        browserName = 'Edge';
      }
      
      setShareCapabilities({
        webShare: hasWebShare,
        clipboard: hasClipboard,
        browserName
      });
    };

    detectCapabilities();
  }, []);

  // Handle share evangelio functionality
  const handleShareEvangelio = async () => {
    if (!gospelData || !currentDate) return;
    
    const shareUrl = `${window.location.origin}/evangelio?date=${currentDate}`;
    const shareTitle = t('shareTitle');
    const shareText = `${shareTitle}*👉${shareUrl}`;
    
    // Update meta tags dynamically for better social sharing
    const updateMetaTags = () => {
      // Update Open Graph title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', shareTitle);
      }
      
      // Update Open Graph description  
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute('content', `${shareText} - ${gospelData?.gospel?.heading || t('gospelTitle')}`);
      }
      
      // Update Open Graph URL
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', shareUrl);
      }
      
      // Update page title temporarily
      document.title = shareTitle;
    };
    
    // Update meta tags before sharing
    updateMetaTags();
    
    // 1. Try Web Share API (best experience on mobile)
    if (navigator.share && navigator.canShare) {
      try {
        const shareData = {
          title: shareTitle,
          text: shareText,
          url: shareUrl
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
          return;
        }
      } catch (err) {
        console.log('Web Share cancelled or failed:', err);
      }
    }
    
    // 2. Clipboard copy (fallback)
    try {
      await navigator.clipboard.writeText(shareText);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 4000);
      return;
    } catch (clipboardErr) {
      console.log('Clipboard API failed:', clipboardErr);
    }

    // 3. Last resort: Manual copy via text selection (old browsers)
    fallbackCopyToClipboard(shareText, shareTitle);
  };
  
  // Fallback copy method for older browsers
  const fallbackCopyToClipboard = (text, title) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 4000);
    } catch (fallbackErr) {
      console.log('Fallback copy failed:', fallbackErr);
      setShareError(t('shareError'));
      setTimeout(() => setShareError(""), 3000);
    }
    
    document.body.removeChild(textArea);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateDate(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateDate(1);
      } else if (e.key === 'Escape') {
        navigate('/');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentDate, navigate]);

  // Initialize and fetch data
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const targetDate = dateParam || getTodayDate();
    fetchGospelData(targetDate);
  }, [searchParams]);

  // Render loading state
  if (loading) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className={`rounded-lg shadow-lg p-8 ${
            darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-lg">{t('loadingGospel')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Navigation Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              <HomeIcon className="h-5 w-5" />
              <span>{t('backToHome')}</span>
            </button>
          </div>

          <div className={`rounded-lg shadow-lg p-8 ${
            darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-amber-700 mb-6">{t('gospelTitle')}</h1>
              <div className={`p-6 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <p className="text-lg">{error}</p>
                <p className="mt-2">{t('tryAgainLater')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render gospel content
  const gospel = gospelData?.gospel || {};
  const cleanedVerses = cleanGospelText(gospel.verses);
  const cleanedIntro = gospel.intro ? gospel.intro
    .replace(/□/g, '')
    .replace(/\u0080\u009c/g, '"')
    .replace(/\u0080\u009d/g, '"') 
    .replace(/\u0080\u0099/g, "'")
    .replace(/\u00c3\u00a9/g, 'é')
    .replace(/\u00c3\u00b1/g, 'ñ')
    .replace(/\u00c3\u00ad/g, 'í')
    .trim() : '';

  const formattedDate = formatDate(gospelData?.date, gospelData?.weekday);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <HomeIcon className="h-5 w-5" />
            <span>{t('backToHome')}</span>
          </button>
        </div>

        {/* Gospel Content */}
        <div className={`rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
          darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}>
          {/* Gospel Header */}
          <div className="p-8 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-4xl font-bold text-center text-amber-700 mb-4">
              {t('gospelTitle')}
            </h1>

            {/* Admin View Count Display */}
            {userType === 'admin' && (
              <div className={`mb-6 p-4 rounded-lg ${
                darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center justify-center space-x-2">
                  <EyeIcon className="h-5 w-5 text-blue-600" />
                  <span className={`font-semibold ${
                    darkMode ? 'text-blue-300' : 'text-blue-800'
                  }`}>
                    {t('adminViews')}
                  </span>
                  {viewCountLoading ? (
                    <span className={`${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {t('adminViewsLoading')}
                    </span>
                  ) : (
                    <span className={`font-bold text-lg ${
                      darkMode ? 'text-blue-200' : 'text-blue-900'
                    }`}>
                      {viewCount !== null ? viewCount.toLocaleString() : '0'}
                    </span>
                  )}
                  <span className={`text-sm ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    {t('adminViewsCount')}
                  </span>
                </div>
              </div>
            )}
            
            {gospel.reference && (
              <p className="text-xl text-center font-semibold text-amber-600 mb-6">
                {gospel.reference}
              </p>
            )}

            {/* Date Navigation */}
            <div className="flex items-center justify-center space-x-4 mb-4">
              <button
                onClick={() => navigateDate(-1)}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                  darkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white hover:scale-105' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700 hover:scale-105'
                }`}
                title={t('previousDay')}
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
              
              <div className={`px-6 py-3 rounded-lg text-center ${
                darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <p className="font-semibold text-lg">{formattedDate}</p>
              </div>
              
              <button
                onClick={() => navigateDate(1)}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                  darkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white hover:scale-105' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700 hover:scale-105'
                }`}
                title={t('nextDay')}
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mobile Navigation and Share Buttons */}
            <div className="md:hidden flex flex-col space-y-4 mt-4">
              {/* Navigation buttons */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigateDate(-1)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {t('previous')}
                </button>
                <button
                  onClick={() => navigateDate(1)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {t('next')}
                </button>
              </div>
              
              {/* Share button for mobile (top) */}
              <div className="flex justify-center">
                <button
                  onClick={handleShareEvangelio}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:transform hover:scale-105 ${
                    darkMode 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  } shadow-md hover:shadow-lg`}
                  title={
                    shareCapabilities.webShare
                      ? t('shareWebTitle')
                      : shareCapabilities.clipboard
                      ? t('shareClipboardTitle')
                      : t('shareFallbackTitle')
                  }
                >
                  <Share2 size={16} className="mr-2" />
                  {shareCapabilities.webShare ? t('share') : t('copyLink')}
                </button>
              </div>
            </div>

            {/* Desktop Share Button (top) */}
            <div className="hidden md:flex justify-center mt-4">
              <button
                onClick={handleShareEvangelio}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:transform hover:scale-105 ${
                  darkMode 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                } shadow-md hover:shadow-lg`}
                title={
                  shareCapabilities.webShare
                    ? t('shareWebTitle')
                    : shareCapabilities.clipboard
                    ? t('shareClipboardTitle')
                    : t('shareFallbackTitle')
                }
              >
                <Share2 size={18} className="mr-2" />
                {t('share')}
              </button>
            </div>
          </div>

          {/* Gospel Body */}
          <div className="p-8 space-y-8">
            {/* Intro Section */}
            {cleanedIntro && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-amber-600">{t('introduction')}</h3>
                <p className="text-lg leading-relaxed">{cleanedIntro}</p>
              </div>
            )}

            {/* Verses Section */}
            {cleanedVerses.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-amber-600">{t('gospel')}</h3>
                <div className="space-y-4">
                  {cleanedVerses.map((verse, index) => (
                    <p key={index} className="text-lg leading-relaxed pl-4 border-l-4 border-amber-200">
                      {verse}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Reflection Section */}
            {gospelData?.reflexion && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-amber-600">{t('reflection')}</h3>
                <div className="reflexion-content">
                  <div
                    className={`leading-relaxed ${
                      darkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}
                    dangerouslySetInnerHTML={{ 
                      __html: gospelData.reflexion 
                    }}
                    style={{
                      lineHeight: '1.7',
                      // Custom styles for HTML elements
                      '--heading-color': darkMode ? '#f59e0b' : '#d97706',
                      '--subheading-color': darkMode ? '#fbbf24' : '#f59e0b',
                      '--accent-color': darkMode ? '#fbbf24' : '#d97706',
                      '--bg-accent': darkMode ? 'rgba(251, 191, 36, 0.1)' : 'rgba(245, 158, 11, 0.1)'
                    }}
                  />
                  
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      .reflexion-content h1 {
                        font-size: 1.75rem !important;
                        font-weight: 700 !important;
                        margin: 1.5rem 0 1rem 0 !important;
                        color: ${darkMode ? '#f59e0b' : '#d97706'} !important;
                      }
                      
                      .reflexion-content h2 {
                        font-size: 1.5rem !important;
                        font-weight: 600 !important;
                        margin: 1.25rem 0 0.75rem 0 !important;
                        color: ${darkMode ? '#fbbf24' : '#f59e0b'} !important;
                      }
                      
                      .reflexion-content h3 {
                        font-size: 1.25rem !important;
                        font-weight: 600 !important;
                        margin: 1rem 0 0.5rem 0 !important;
                        color: ${darkMode ? '#fbbf24' : '#f59e0b'} !important;
                      }
                      
                      .reflexion-content p {
                        margin: 1rem 0 !important;
                        text-align: justify !important;
                      }
                      
                      .reflexion-content strong {
                        font-weight: 600 !important;
                        color: ${darkMode ? '#fbbf24' : '#d97706'} !important;
                      }
                      
                      .reflexion-content em {
                        font-style: italic !important;
                      }
                      
                      .reflexion-content ul, .reflexion-content ol {
                        margin: 1rem 0 !important;
                        padding-left: 2rem !important;
                      }
                      
                      .reflexion-content li {
                        margin: 0.5rem 0 !important;
                      }
                      
                      .reflexion-content blockquote {
                        border-left: 4px solid ${darkMode ? '#fbbf24' : '#f59e0b'} !important;
                        padding-left: 1rem !important;
                        margin: 1.5rem 0 !important;
                        font-style: italic !important;
                        background: ${darkMode ? 'rgba(251, 191, 36, 0.1)' : 'rgba(245, 158, 11, 0.1)'} !important;
                        padding: 1rem !important;
                        border-radius: 0.375rem !important;
                      }
                    `
                  }} />
                </div>
              </div>
            )}

            {/* Links Section */}
            {gospel.links && gospel.links.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-amber-600">{t('relatedLinks')}</h3>
                <div className="space-y-3">
                  {gospel.links.map((link, index) => {
                    // Parse link format: "Text in brackets (actual_url)"
                    // Extract text in brackets and URL in parentheses
                    const bracketMatch = link.match(/\[([^\]]+)\]/);
                    const parenthesesMatch = link.match(/\(([^)]+)\)/);
                    
                    const linkText = bracketMatch ? bracketMatch[1] : t('defaultLinkText');
                    const linkUrl = parenthesesMatch ? parenthesesMatch[1] : link;
                    
                    return (
                      <div key={index}>
                        <a
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`block px-3 py-2 rounded-md transition-all duration-200 transform hover:translate-x-1 ${
                            darkMode 
                              ? 'text-blue-300 bg-blue-900/20 hover:bg-blue-800/30' 
                              : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                          }`}
                          style={{
                            textDecoration: 'none'
                          }}
                        >
                          {linkText}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Metadata Section */}
            {(gospelData?.liturgical_note || gospelData?.url || gospelData?.copyright_note) && (
              <div className={`p-6 rounded-lg ${
                darkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <h3 className="text-lg font-semibold mb-4 text-amber-600">{t('additionalInfo')}</h3>
                <div className="space-y-3">
                  {gospelData.liturgical_note && (
                    <div>
                      <strong>{t('liturgicalNote')}</strong> {gospelData.liturgical_note}
                    </div>
                  )}
                  
                  {gospelData.url && (
                    <div>
                      <strong>{t('source')}</strong>{' '}
                      <a
                        href={gospelData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${
                          darkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                        } underline`}
                      >
                        {t('viewOriginalSource')}
                      </a>
                    </div>
                  )}
                  
                  {gospelData.copyright_note && (
                    <div>
                      <strong>{t('rights')}</strong> {gospelData.copyright_note}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Share Button */}
        <div className="flex justify-center mt-8">
          <button
            onClick={handleShareEvangelio}
            className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:transform hover:scale-105 ${
              darkMode 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
            } shadow-md hover:shadow-lg`}
            title={
              shareCapabilities.webShare
                ? t('shareWebTitle')
                : shareCapabilities.clipboard
                ? t('shareClipboardTitle')
                : t('shareFallbackTitle')
            }
          >
            <Share2 size={18} className="mr-2" />
            {t('share')}
          </button>
        </div>

        {/* Back to Home Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <HomeIcon className="h-5 w-5" />
            <span>← {t('backToHome')}</span>
          </button>
        </div>

        {/* Share Success/Error Messages */}
        {shareSuccess && (
          <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse">
            {t('shareSuccess')}
          </div>
        )}
        
        {shareError && (
          <div className="fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
            ❌ {shareError}
          </div>
        )}

        {/* Keyboard shortcuts help */}
        <div className={`mt-6 text-center text-sm ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          <p>{t('keyboardShortcuts')}</p>
        </div>
      </div>
    </div>
  );
};

export default EvangelioPage;